import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { endOfWeek, format, startOfWeek } from 'date-fns';

import { addPerson, gear } from '@/assets/icons';
import { InviteTeamMemberDialog } from '@/components/shared/InviteTeamMemberDialog';
import { RoundedButton } from '@/components/shared/RoundedButton/RoundedButton';
import { SheetLayout } from '@/components/shared/SheetLayout';
import { UserTopSongs } from '@/components/UserTopSongs';
import { TeamMilestones } from '@/config/constants';
import { setlistsUrl } from '@/config/urls';
import { Permissions, useTestNotificationMutation } from '@/graphql';
import { classNames } from '@/helpers/classNames';
import { useFormattedTitle } from '@/hooks/useFormattedTitle';
import { DashboardHeader } from '@/pages/Dashboard/components/DashboardHeader/DashboardHeader';
import {
	useAnalyticsEvent,
	useFireAnalyticsPageView,
} from '@/providers/AnalyticsProvider';
import { useAuth } from '@/providers/AuthProvider';
import { useSetlists } from '@/providers/SetlistsProvider';
import { useEnvironment } from '@/providers/EnvironmentProvider';
import { useIntercom } from '@/providers/IntercomProvider/IntercomProvider';
import { useLayout } from '@/providers/LayoutProvider';
import { useSubscription } from '@/providers/SubscriptionProvider';
import { usePermissions } from '@/providers/PermissionsProvider';
import { useTeamActionSubscription } from '@/providers/TeamActionSubscription';
import { useTeam } from '@/providers/TeamProvider';
import { Capacitor } from '@capacitor/core';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router';

import { CompleteProfilePrompt } from './components/CompleteProfilePrompt/CompleteProfilePrompt';
import { DashboardSetlists } from './components/DashboardSetlists';
import { FAQPanel } from './components/FaqPanel';
import { GettingStarted } from './components/GettingStarted/GettingStarted';
import { LatestMessages } from './components/LatestMessages';
import { WeeklyPanel } from './components/WeeklyPanel/WeeklyPanel';
import ReelThumbnailCard from './components/ReelThumbnail/ReelThumbnailCard';
import styles from './Dashboard.module.scss';

const isCapacitor = Capacitor.isNativePlatform();
// This page URL is defined as a different one for analytics logging purposes
const pageUrl = '/dashboard';

export const Dashboard: React.FC = () => {
	const { hasMilestones, team } = useTeam();
	const hasTeammates = team.members.length > 1;
	const { hasPermission } = usePermissions();
	const { setAccountOpen, openSetlistGenerate, shootFireworks, openBottomCard } = useLayout();
	const { openIntercom } = useIntercom();
	const { setPaidModalOpen } = useSubscription();
	const { isConnected, log } = useTeamActionSubscription();
	const [title] = useFormattedTitle('Asaph | Home');
	const navigate = useNavigate();

	const { mutateAsync: sendTestNotification } = useTestNotificationMutation();
	const { token, user } = useAuth();
	const { api } = useEnvironment();
	const { setlists, isLoading: setlistsLoading } = useSetlists();
	const [nudgeResult, setNudgeResult] = useState<string | null>(null);
	const [nudgeSending, setNudgeSending] = useState(false);
	const [recapResult, setRecapResult] = useState<string | null>(null);
	const [recapSending, setRecapSending] = useState(false);

	const [addTeamMemberOpen, setAddTeamMemberOpen] = useState<boolean>(false);

	const fireOpenInviteEvent = useAnalyticsEvent('Dashboard_Open_Invite');
	const fireCycleComplete = useAnalyticsEvent('WeeklyPanel_Cycle_Complete');
	const fireOpenSetlitstEvent = useAnalyticsEvent(
		'Dashboard_Navigate_Setlits',
	);
	const fireOpenSetlistGenerate = useAnalyticsEvent(
		'Layout_Open_Generate_Settings_Bottom_Panel',
	);

	useFireAnalyticsPageView({
		enabled: !!title,
		pageViewArgs: {
			title,
			url: pageUrl,
		},
	});

	const gettingStartedComplete = useMemo(
		() =>
			hasMilestones([
				TeamMilestones.ADDED_INITIAL_SONGS,
				TeamMilestones.CREATED_FIRST_SETLIST,
			]),
		[hasMilestones],
	);

	const hasCompletedCycle = useMemo(
		() => setlists.some(s => s.submissions?.some(sub => sub.userId === user?.id)),
		[setlists, user?.id],
	);

	const isTeamAdmin = team.members.find(m => m.user.id === user?.id)?.roles.includes('teamAdmin') ?? false;

	const weeklySetlists = useMemo(() => {
		const now = new Date();
		const monday = startOfWeek(now, { weekStartsOn: 1 });
		const sunday = endOfWeek(now, { weekStartsOn: 1 });
		return setlists.filter(s => {
			const startAt = new Date(s.startAt);
			return monday <= startAt && startAt <= sunday;
		});
	}, [setlists]);

	const currentSetlist = useMemo(() => {
		if (weeklySetlists.length > 0) {
			return weeklySetlists.reduce((latest, current) =>
				new Date(current.startAt) > new Date(latest.startAt) ? current : latest,
			);
		}
		const now = new Date();
		const futureSetlists = setlists.filter(
			s => !s.draft && new Date(s.startAt) > now,
		);
		if (futureSetlists.length === 0) return null;
		return futureSetlists.reduce((soonest, current) =>
			new Date(current.startAt) < new Date(soonest.startAt) ? current : soonest,
		);
	}, [weeklySetlists, setlists]);

	// Wait for the initial data load to settle before tracking cycle transitions.
	// Without this, existing users see confetti because setlists starts as [] (isLoading=true),
	// making hasCompletedCycle=false at mount, then flipping true when data arrives.
	const setlistsSettled = useRef(false);
	const prevHasCompletedCycle = useRef(false);
	useEffect(() => {
		if (setlistsLoading) return;

		if (!setlistsSettled.current) {
			// First settled load: record baseline, never celebrate on initial data
			setlistsSettled.current = true;
			prevHasCompletedCycle.current = hasCompletedCycle;
			return;
		}

		if (hasCompletedCycle && !prevHasCompletedCycle.current) {
			shootFireworks();
			fireCycleComplete({ weekOf: format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd') });
			setTimeout(() => {
				openBottomCard({ title: 'First cycle complete! 🎉', text: "You've completed your first full week on Asaph." });
			}, 600);
		}
		prevHasCompletedCycle.current = hasCompletedCycle;
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasCompletedCycle, setlistsLoading]);

	const openInvite = () => {
		setAddTeamMemberOpen(true);
		fireOpenInviteEvent();
	};
	const openAccount = () =>
		setAccountOpen({
			open: true,
		});

	return (
		<>
			<Helmet title={title} />
			<SheetLayout
				actions={[
					{
						icon: gear,
						onClick: openAccount,
					},
					{
						icon: addPerson,
						onClick: openInvite,
					},
				]}
				titleComponent={
					<DashboardHeader onProfileClick={openAccount} />
				}
				//topComponent={<DashboardUsagePanel />}
			>
				<div className={styles['root']}>
					{!gettingStartedComplete && <GettingStarted />}
					{gettingStartedComplete && isTeamAdmin && !hasCompletedCycle && !!currentSetlist && (
						<WeeklyPanel
							onInviteClick={openInvite}
							currentSetlist={currentSetlist}
						/>
					)}
					{gettingStartedComplete && hasCompletedCycle && <CompleteProfilePrompt />}
					{gettingStartedComplete && hasTeammates && (
						<LatestMessages openInvite={openInvite} />
					)}

					{/* <RecentlyWatchedPanel /> */}
					{/* <DevotionPanel /> */}

					{/* Commented out: covered by Give Feedback CTA on setlist card chip */}
					{/* {hasMilestones([TeamMilestones.CREATED_FIRST_SETLIST]) && (
						<DashboardFeedbackSetlists />
					)} */}

					<DashboardSetlists
						onViewAllClick={() => {
							navigate(setlistsUrl);
							fireOpenSetlitstEvent();
						}}
						onGenerateClick={() => {
							fireOpenSetlistGenerate({
								url: setlistsUrl,
							});
							openSetlistGenerate();
						}}
						hasViewAll={gettingStartedComplete}
					/>

					{user?.id && (
						<UserTopSongs userId={user.id} variant="dashboard" />
					)}

					<ReelThumbnailCard />
					{/* {gettingStartedComplete && <DashboardCollections />} */}
					{/* <AuthorsList /> */}
					<FAQPanel toggleMessenger={openIntercom} />

					{/* Dev Tools */}
					{hasPermission(Permissions.DevtoolsRead) && (
						<div className="mb-lg">
							<div className={styles['title']}>Dev Tools</div>

							<div className={classNames([styles['dev-tools-subheading'], 'mt-sm'])}>
								UI
							</div>
							<div className={classNames([styles['dev-tools-row'], 'mt-sm'])}>
								<RoundedButton
									color="default"
									onClick={() => setPaidModalOpen(true)}
								>
									🧪 Fire PaidModal
								</RoundedButton>
							</div>

							<div className={classNames([styles['dev-tools-subheading'], 'mt-lg'])}>
								Notifications/email
							</div>
							<div className={classNames([styles['dev-tools-row'], 'mt-sm'])}>
								<RoundedButton
									color="default"
									onClick={() => sendTestNotification({})}
								>
									Send test notification to me
								</RoundedButton>
								<RoundedButton
									color="default"
									onClick={() =>
										sendTestNotification({
											input: {
												delay: 5000,
											},
										})
									}
								>
									Send test notification to me with a delay
								</RoundedButton>
								<RoundedButton
									color="default"
									disabled={nudgeSending}
									onClick={async () => {
										setNudgeSending(true);
										setNudgeResult(null);
										try {
											const res = await axios.post<{
												sent: number;
												total: number;
												errors: string[];
											}>(
												`${api.url}/dev/trigger-nudge`,
												{},
												{ headers: { Authorization: `Bearer ${token}` } },
											);
											setNudgeResult(
												`Sent ${res.data.sent}/${res.data.total} nudge(s)${res.data.errors.length ? ` — ${res.data.errors.length} error(s)` : ''}`,
											);
										} catch (e: any) {
											const detail = e?.response?.data?.error ?? String(e);
											setNudgeResult(`Error: ${detail}`);
										} finally {
											setNudgeSending(false);
										}
									}}
								>
									{nudgeSending ? 'Sending…' : 'Trigger re-engagement nudge'}
								</RoundedButton>
								<RoundedButton
									color="default"
									disabled={recapSending}
									onClick={async () => {
										setRecapSending(true);
										setRecapResult(null);
										try {
											// The digest always reports the PRIOR completed UTC
											// calendar week relative to `now` (D-14/D-15) — nudging
											// `now` 7 days forward makes today's actual
											// (in-progress) week become that prior week, so
											// same-day testing shows real data instead of zeros.
											const now = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
											await axios.post(
												`${api.url}/dev/trigger-weekly-recap`,
												{ now: now.toISOString() },
												{ headers: { Authorization: `Bearer ${token}` } },
											);
											setRecapResult('Weekly AARRR recap sent to alex@asaph.io and ikoifko@gmail.com (this week)');
										} catch (e: any) {
											const detail = e?.response?.data?.error ?? String(e);
											setRecapResult(`Error: ${detail}`);
										} finally {
											setRecapSending(false);
										}
									}}
								>
									{recapSending ? 'Sending…' : 'Trigger weekly AARRR recap'}
								</RoundedButton>
							</div>

							<div className={classNames([styles['dev-tools-subheading'], 'mt-lg'])}>
								Data reset
							</div>
							<div className={classNames([styles['dev-tools-row'], 'mt-sm'])}>
								<RoundedButton
									color="default"
									onClick={() => {
										const weekOf = format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd');
										localStorage.removeItem(`weeklyPanel_songsAdded_${weekOf}`);
										localStorage.removeItem(`weeklyPanel_setlistEdited_${weekOf}`);
										window.location.reload();
									}}
								>
									Reset WeeklyPanel steps (this week)
								</RoundedButton>
							</div>

							{nudgeResult && (
								<div className="mt-sm">{nudgeResult}</div>
							)}
							{recapResult && (
								<div className="mt-sm">{recapResult}</div>
							)}

							<div className={classNames([styles['title'], 'mt-lg'])}>
								WS Log
							</div>
							<div className="mb-sm mt-sm">
								WS Connected: {isConnected.toString()}
							</div>
							<div className="my-lg">
								{log.map((m, i) => (
									<div key={`message-${i}`}>{m}</div>
								))}
							</div>
						</div>
					)}
					{/* <-- END Dev Tools */}
				</div>
			</SheetLayout>

			<InviteTeamMemberDialog
				open={addTeamMemberOpen}
				onClose={() => setAddTeamMemberOpen(false)}
			/>
		</>
	);
};
